import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import api from "../../lib/api";

function trendOf(vals, higherIsBetter = true) {
  const v = vals.filter((x) => x != null);
  if (v.length < 2) return { label: "not enough boards", Icon: Minus, cls: "text-slate-400" };
  const first = v.slice(0, Math.ceil(v.length / 2)).reduce((a, b) => a + b, 0) / Math.ceil(v.length / 2);
  const last = v.slice(Math.floor(v.length / 2)).reduce((a, b) => a + b, 0) / (v.length - Math.floor(v.length / 2));
  const delta = (last - first) * (higherIsBetter ? 1 : -1);
  const thresh = Math.max(2, Math.abs(first) * 0.08);
  if (delta > thresh) return { label: "improving", Icon: TrendingUp, cls: "text-green-600" };
  if (delta < -thresh) return { label: "slipping", Icon: TrendingDown, cls: "text-red-600" };
  return { label: "steady", Icon: Minus, cls: "text-amber-600" };
}

export function DeliveryTrend({ interviews: given }) {
  const [rows, setRows] = useState(given || null);
  useEffect(() => {
    if (given) { setRows(given); return; }
    api.get("/interview/history").then(({ data }) => setRows(data.interviews || [])).catch(() => setRows([]));
  }, [given]);
  if (!rows) return null;
  const boards = rows.filter((i) => i.status === "completed" && i.scores).sort((a, b) => (a.completed_at || a.created_at).localeCompare(b.completed_at || b.created_at)).slice(-5);
  if (boards.length < 2) return null;
  const data = boards.map((b, i) => ({
    name: `#${i + 1}`, date: new Date(b.completed_at || b.created_at).toLocaleDateString(),
    overall: b.scores.overall, fillers: b.scores.delivery?.filler_rate ?? null, wpm: b.scores.delivery?.wpm ?? null,
    confidence: b.scores.delivery?.confidence ?? null,
  }));
  const trends = [
    ["Overall score", trendOf(data.map((d) => d.overall))],
    ["Filler words", trendOf(data.map((d) => d.fillers), false)],
    ["Confidence", trendOf(data.map((d) => d.confidence))],
    ["Pace", trendOf(data.map((d) => d.wpm == null ? null : -Math.abs(d.wpm - 150)))],
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="delivery-trend">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Delivery trend · last {data.length} boards</p>
        <div className="flex flex-wrap gap-3">
          {trends.map(([l, t]) => (
            <span key={l} className={`inline-flex items-center gap-1 text-xs font-semibold ${t.cls}`} data-testid={`trend-${l.toLowerCase().replace(/\s+/g, "-")}`}><t.Icon className="w-3.5 h-3.5" /> {l}: {t.label}</span>
          ))}
        </div>
      </div>
      <div className="h-56 min-h-[224px]" style={{ minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis yAxisId="pct" domain={[0, 100]} tick={{ fontSize: 12 }} />
            <YAxis yAxisId="wpm" orientation="right" domain={[60, 220]} tick={{ fontSize: 12 }} />
            <Tooltip labelFormatter={(l, p) => `${l} · ${p?.[0]?.payload?.date || ""}`} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="pct" type="monotone" dataKey="overall" name="Overall" stroke="#b91c1c" strokeWidth={2.5} dot={{ r: 4 }} />
            <Line yAxisId="pct" type="monotone" dataKey="confidence" name="Confidence" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line yAxisId="pct" type="monotone" dataKey="fillers" name="Fillers /100w" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line yAxisId="wpm" type="monotone" dataKey="wpm" name="Pace (wpm)" stroke="#64748b" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
